import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  keywords?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  breadcrumbs?: { name: string; url: string }[];
}

const SEOHead = ({ title, description, canonical, ogImage, keywords, ogType, jsonLd, breadcrumbs }: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    if (keywords) {
      setMeta("keywords", keywords);
    }
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", ogType || "website", "property");
    setMeta("og:site_name", "LAANDED Property Group", "property");
    setMeta("og:locale", "en_AU", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title, "name");
    setMeta("twitter:description", description, "name");

    if (canonical) {
      setMeta("og:url", canonical, "property");
    }

    if (ogImage) {
      setMeta("og:image", ogImage, "property");
      setMeta("twitter:image", ogImage, "name");
    }

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    } else if (link) {
      link.remove();
    }

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach((s) => s.remove());

    const allJsonLd: Record<string, unknown>[] = [];

    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      allJsonLd.push(...items);
    }

    // BreadcrumbList schema
    if (breadcrumbs && breadcrumbs.length > 0) {
      allJsonLd.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((bc, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": bc.name,
          "item": bc.url,
        })),
      });
    }

    // Speakable schema for the page
    if (canonical) {
      allJsonLd.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": title,
        "url": canonical,
        "isPartOf": { "@id": `${BASE_URL}/#website` },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", ".body-large", "meta[name='description']"],
        },
      });
    }

    allJsonLd.forEach((item) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });

    return () => {
      const scripts = document.querySelectorAll('script[data-seo-jsonld]');
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, canonical, ogImage, keywords, ogType, jsonLd, breadcrumbs]);

  return null;
};

export default SEOHead;

// Shared JSON-LD fragments
export const LAANDED_ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness", "HomeAndConstructionBusiness"],
  "additionalType": ["https://schema.org/RealEstateAgent"],
  "@id": "https://www.laanded.com.au/#organization",
  "name": "LAANDED Property Group",
  "legalName": "LAANDED Property Group",
  "url": "https://www.laanded.com.au",
  "description": "LAANDED is a privately led Australian property development and investment company. We deploy capital strategically across development, investment, and asset management, delivering land subdivisions, mixed-use precincts, seniors living and hotel projects across NSW.",
  "foundingLocation": "Sydney, NSW, Australia",
  "areaServed": [
    { "@type": "City", "name": "Sydney" },
    { "@type": "State", "name": "New South Wales" },
    { "@type": "Country", "name": "Australia" },
  ],
  "serviceType": ["Property Development", "Property Investment", "Real Estate Advisory", "Asset Management", "Land Subdivision", "Mixed-Use Development", "Seniors Living Development"],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "101/377 New South Head Road",
    "addressLocality": "Double Bay",
    "addressRegion": "NSW",
    "postalCode": "2028",
    "addressCountry": "AU",
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": -33.8736,
    "longitude": 151.2266,
  },
  "telephone": "+61299095611",
  "email": "hello@laanded.com.au",
  "sameAs": ["https://www.linkedin.com/in/peter-manettas-839b3224b", "https://www.linkedin.com/company/laanded"],
  "knowsAbout": ["Property Development", "Real Estate Investment", "Land Subdivision", "Mixed-Use Development", "Seniors Living", "Hotel Development"],
};

export const BASE_URL = "https://www.laanded.com.au";
export const OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/DIvm4HLOHvdk05lgvdu2wpKPenz1/social-images/social-1772448797514-laanded-laandedpropertygroup-NSW.webp";
